import { type FormEvent, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LMS_QUESTIONS_AI_PROMPT, LMS_QUESTIONS_EXAMPLE } from '@lms/shared-types';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';

interface QuestionBank {
  id: string;
  title: string;
}

export function LegacyXhtmlImportRedirect() {
  const [params] = useSearchParams();
  const search = params.toString();
  return <Navigate to={search ? `/preguntas/importar?${search}` : '/preguntas/importar'} replace />;
}

export function ImportQuestionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const preselectedBank = searchParams.get('bankId') ?? '';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const backUrl = preselectedBank ? `/preguntas?bankId=${preselectedBank}` : '/preguntas';

  const [bankId, setBankId] = useState(preselectedBank);
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');

  const { data: banks, isLoading: banksLoading } = useQuery({
    queryKey: ['question-banks'],
    queryFn: () => api<{ data: QuestionBank[] }>('/questions/banks'),
  });

  const importMutation = useMutation({
    mutationFn: (payload: { content: string; bankId: string }) =>
      api<{ imported: number; parsed: number; errors?: string[] }>('/questions/import/json', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: async (result, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['questions'] }),
        queryClient.invalidateQueries({ queryKey: ['question-banks'] }),
      ]);
      if (result.errors?.length) {
        window.alert(result.errors.join('\n\n'));
      }
      const params = new URLSearchParams();
      if (variables.bankId) params.set('bankId', variables.bankId);
      params.set('imported', String(result.imported));
      navigate(`/preguntas?${params.toString()}`);
    },
    onError: (err) => setFormError((err as Error).message),
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setFileName('');
      setFileContent('');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setFileContent(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsText(file);
  };

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const finalContent = content.trim() || fileContent.trim();
    if (!bankId) {
      setFormError('Selecciona un banco de destino.');
      return;
    }
    if (!finalContent) {
      setFormError('Sube un archivo JSON o pega el contenido a importar.');
      return;
    }
    setFormError(null);
    importMutation.mutate({ content: finalContent, bankId });
  }

  function downloadTemplate() {
    const blob = new Blob([`${JSON.stringify(LMS_QUESTIONS_EXAMPLE, null, 2)}\n`], {
      type: 'application/json',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'lms-questions.ejemplo.json';
    link.click();
    URL.revokeObjectURL(link.href);
  }

  async function copyAiPrompt() {
    try {
      await navigator.clipboard.writeText(LMS_QUESTIONS_AI_PROMPT);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('error');
    }
  }

  const fileMeta = fileName || 'Archivos .json soportados';

  return (
    <div className="lms-aiken-import lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to={backUrl} label="Volver a Bancos de Preguntas" />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Importar preguntas JSON</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Genera o migra el archivo con un modelo de IA y súbelo aquí.
          </p>
        </div>
      </div>

      <form className="lms-aiken-import__card" onSubmit={handleSubmit}>
        <div className="lms-aiken-import__body">
          <div className="lms-aiken-import__field">
            <label htmlFor="question_bank_id" className="lms-aiken-import__label">
              Banco de destino <span className="lms-aiken-import__required">*</span>
            </label>
            <div className="lms-aiken-import__select-wrap">
              <select
                id="question_bank_id"
                name="question_bank_id"
                required
                value={bankId}
                onChange={(event) => setBankId(event.target.value)}
                className="lms-aiken-import__select"
                disabled={banksLoading}
              >
                <option value="" disabled>
                  Selecciona un banco
                </option>
                {(banks?.data ?? []).map((bank) => (
                  <option key={bank.id} value={bank.id}>
                    {bank.title}
                  </option>
                ))}
              </select>
              <span className="material-symbols-outlined lms-aiken-import__select-icon" aria-hidden="true">
                expand_more
              </span>
            </div>
            <p className="lms-aiken-import__hint">Las preguntas se añadirán a este banco.</p>
          </div>

          <div className="lms-aiken-import__field">
            <span className="lms-aiken-import__label">Plantilla e instrucciones para IA</span>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="lms-aiken-import__btn lms-aiken-import__btn--cancel" onClick={downloadTemplate}>
                Descargar plantilla
              </button>
              <button type="button" className="lms-aiken-import__btn lms-aiken-import__btn--cancel" onClick={() => void copyAiPrompt()}>
                {copyState === 'copied' ? 'Prompt copiado' : 'Copiar prompt para IA'}
              </button>
            </div>
            {copyState === 'error' ? (
              <p className="lms-aiken-import__error" role="alert">
                No se pudo copiar el prompt. Cópialo manualmente desde la documentación del formato.
              </p>
            ) : null}
          </div>

          <div className="lms-aiken-import__divider" />

          <div className="lms-aiken-import__field">
            <span className="lms-aiken-import__label">Archivo JSON</span>
            <p className="lms-aiken-import__hint lms-aiken-import__hint--spaced">
              Archivo <code>lms-questions</code> generado por IA o exportado desde este LMS.
            </p>

            <label htmlFor="import_file" className="lms-aiken-import__dropzone group">
              <span
                className="material-symbols-outlined lms-aiken-import__dropzone-icon"
                style={{ fontVariationSettings: "'wght' 300" }}
                aria-hidden="true"
              >
                upload_file
              </span>
              <div className="lms-aiken-import__dropzone-text">
                <span className="lms-aiken-import__dropzone-link">Selecciona un archivo</span>
                <span>o arrastra y suelta aquí</span>
              </div>
              <p className="lms-aiken-import__dropzone-meta">{fileMeta}</p>
              <input
                ref={fileInputRef}
                id="import_file"
                name="import_file"
                type="file"
                accept=".json,application/json"
                className="sr-only"
                onChange={handleFileChange}
              />
            </label>
          </div>

          <div className="lms-aiken-import__or">
            <span className="lms-aiken-import__or-line" />
            <span className="lms-aiken-import__or-label">O</span>
            <span className="lms-aiken-import__or-line" />
          </div>

          <div className="lms-aiken-import__field">
            <label htmlFor="import_content" className="lms-aiken-import__label">
              Contenido JSON
            </label>
            <p className="lms-aiken-import__hint lms-aiken-import__hint--spaced">
              Pega directamente el JSON <code>lms-questions</code>.
            </p>
            <textarea
              id="import_content"
              name="import_content"
              rows={8}
              value={content}
              onChange={(event) => setContent(event.target.value)}
              className="lms-aiken-import__textarea"
              placeholder='{"format":"lms-questions","schemaVersion":1,"questions":[]}'
            />
          </div>

          {formError && (
            <p className="lms-aiken-import__error" role="alert">
              {formError}
            </p>
          )}
        </div>

        <div className="lms-aiken-import__footer">
          <Link to={backUrl} className="lms-aiken-import__btn lms-aiken-import__btn--cancel">
            Cancelar
          </Link>
          <button type="submit" className="lms-aiken-import__btn lms-aiken-import__btn--submit" disabled={importMutation.isPending}>
            {importMutation.isPending ? 'Importando…' : 'Importar preguntas'}
          </button>
        </div>
      </form>
    </div>
  );
}
